import { FileMagnifyingGlass } from "@phosphor-icons/react";
import {
  IconButton,
  Popover,
  PopoverArrow,
  PopoverDisclosure,
  PopoverProvider,
} from "@replicate/ui";
import { useQuery } from "@tanstack/react-query";
import mime from "mime";
import { useMemo } from "react";
import ReactPlayer from "react-player";
import { getMimeTypeFromUrl } from "../utils/file-types";

export default function PredictionOutputPreview({
  url,
}: {
  url: string;
}) {
  const syncMimeType = url ? mime.getType(url) : null;

  // Fall back to the content-type proxy for URLs that don't have a resolvable
  // extension (e.g. URLs with query strings).
  const { data: fetchedMimeType } = useQuery({
    queryKey: ["prediction-output-preview-mime-type", url],
    queryFn: ({ signal }) => getMimeTypeFromUrl(url, signal),
    enabled: Boolean(url) && !syncMimeType,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const previewComponent = useMemo(() => {
    if (!url) return null;

    const mimeType = syncMimeType ?? fetchedMimeType;
    if (!mimeType) return null;

    if (mimeType.startsWith("image")) {
      return <img src={url} className="w-full" />;
    }

    if (mimeType.startsWith("video")) {
      return (
        <ReactPlayer
          url={url}
          controls={true}
          playsinline
          playing={true}
          muted={true}
          loop={true}
          width="auto"
          height="auto"
          className="w-full"
        />
      );
    }
  }, [url, syncMimeType, fetchedMimeType]);

  if (!previewComponent) return null;

  return (
    <PopoverProvider placement="right">
      <PopoverDisclosure render={<IconButton variant="clear" size="sm" />}>
        <FileMagnifyingGlass />
      </PopoverDisclosure>
      <Popover className="p-2 z-[1]" fitViewport flip unmountOnHide>
        <PopoverArrow />
        <div className="max-w-80">{previewComponent}</div>
      </Popover>
    </PopoverProvider>
  );
}
